import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Search, Phone, Plus, User, CalendarClock, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

export default function PatientsList() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", date_of_birth: "", codice_fiscale: "", allergies: "", diseases: "", treatments_interest: "", notes: "" });
  const [bookingFor, setBookingFor] = useState(null);
  const navigate = useNavigate();

  // Doctor's treatment catalog, normalized to {name, duration_min}
  const catalog = (user?.treatments || []).map((x) =>
    typeof x === "string" ? { name: x, duration_min: user?.slot_duration || 30 } : x
  );

  useEffect(() => { load(); }, []);
  async function load() {
    const { data } = await api.get(`/patients?q=${encodeURIComponent(q)}`);
    setPatients(data);
  }
  async function submit(e) {
    e.preventDefault();
    try {
      const { data } = await api.post("/patients", form);
      toast.success(t("patients.created"));
      setShowNew(false);
      setForm({ full_name: "", phone: "", email: "", date_of_birth: "", codice_fiscale: "", allergies: "", diseases: "", treatments_interest: "", notes: "" });
      navigate(`/pacientes/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    }
  }

  return (
    <div className="space-y-8 fade-in-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("patients.eyebrow")}</p>
          <h1 className="font-heading text-5xl font-light">{t("patients.title")}</h1>
        </div>
        <button data-testid="btn-new-patient" onClick={() => setShowNew(!showNew)}
          className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors duration-300 font-medium">
          <Plus className="w-4 h-4" strokeWidth={1.75} /> {t("patients.new")}
        </button>
      </div>

      {showNew && (
        <form onSubmit={submit} data-testid="new-patient-form" className="bg-white border border-stone-200 rounded-2xl p-8 space-y-4">
          <h3 className="font-heading text-2xl font-light mb-2">{t("patients.new")}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t("patients.fullName")} required value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testId="input-full-name" />
            <Field label={t("common.phone")} required value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+34 600 000 000" testId="input-phone" />
            <Field label={t("common.email")} value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="input-email" />
            <Field label={t("patients.dob")} type="date" value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} testId="input-dob" />
            <Field label={t("patients.cf")} value={form.codice_fiscale} onChange={(v) => setForm({ ...form, codice_fiscale: v })} testId="input-cf" />
            <Field label={t("patients.allergies")} value={form.allergies} onChange={(v) => setForm({ ...form, allergies: v })} testId="input-allergies" />
            <Field label={t("patients.diseases")} value={form.diseases} onChange={(v) => setForm({ ...form, diseases: v })} testId="input-diseases" />
            <Field label={t("patients.treatmentInterest")} value={form.treatments_interest} onChange={(v) => setForm({ ...form, treatments_interest: v })} testId="input-treatments" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.notes")}</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
              spellCheck={true} autoCorrect="on" autoCapitalize="sentences"
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373]" />
          </div>
          <div className="flex gap-3">
            <button type="submit" data-testid="submit-new-patient" className="px-6 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800">{t("common.save")}</button>
            <button type="button" onClick={() => setShowNew(false)} className="px-6 py-2.5 rounded-full border border-stone-200 hover:bg-stone-50">{t("common.cancel")}</button>
          </div>
        </form>
      )}

      <div className="flex items-center gap-3 bg-white border border-stone-200 rounded-full px-5 py-3 max-w-xl">
        <Search className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
        <input placeholder={t("patients.search")} value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="patient-search"
          onKeyDown={(e) => e.key === "Enter" && load()}
          className="flex-1 bg-transparent outline-none text-sm" />
        <button onClick={load} className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-stone-900">{t("common.search")}</button>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        {patients.length === 0 ? (
          <div className="py-24 text-center text-stone-500">
            <User className="w-8 h-8 mx-auto mb-3 text-stone-300" strokeWidth={1} />
            <p className="text-sm">{t("patients.empty")}</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-xs uppercase tracking-[0.15em] text-stone-500 border-b border-stone-100">
                <th className="text-left px-6 py-4">{t("patients.colName")}</th>
                <th className="text-left px-6 py-4 hidden md:table-cell">{t("patients.colPhone")}</th>
                <th className="text-left px-6 py-4 hidden lg:table-cell">{t("patients.colTreatment")}</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="stagger">
              {patients.map((p) => (
                <tr key={p.id} data-testid={`patient-row-${p.id}`}
                  className={`border-b border-stone-50 hover:bg-stone-50 cursor-pointer transition-colors ${p.has_booked === false ? "bg-amber-50/40" : ""}`}
                  onClick={() => navigate(`/pacientes/${p.id}`)}>
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-stone-900">{p.full_name}</span>
                      {p.has_booked === false && (
                        <span data-testid={`patient-notbooked-${p.id}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-medium text-amber-800 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">
                          {t("patients.notBookedYet") || "Non prenotato ancora"}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-stone-500">{p.email || "—"}</div>
                  </td>
                  <td className="px-6 py-5 hidden md:table-cell text-sm text-stone-600">
                    <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" strokeWidth={1.5} /> {p.phone}</div>
                  </td>
                  <td className="px-6 py-5 hidden lg:table-cell text-sm text-stone-600">{p.treatments_interest || "—"}</td>
                  <td className="px-6 py-5 text-right whitespace-nowrap">
                    <button data-testid={`btn-book-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); setBookingFor(p); }}
                      className="inline-flex items-center gap-1.5 mr-3 text-xs uppercase tracking-[0.2em] text-stone-700 hover:text-stone-900 border border-stone-200 hover:border-stone-400 rounded-full px-3 py-1.5 transition-colors">
                      <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.75} /> {t("patients.book") || "Prenota"}
                    </button>
                    <Link to={`/pacientes/${p.id}`} className="text-xs uppercase tracking-[0.2em] text-[#D4A373] hover:text-[#B07D4F]">{t("patients.viewDetails")} →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {bookingFor && (
        <QuickBookModal
          patient={bookingFor}
          catalog={catalog}
          onClose={() => setBookingFor(null)}
          onDone={() => { setBookingFor(null); load(); }}
        />
      )}
    </div>
  );
}

function QuickBookModal({ patient, catalog, onClose, onDone }) {
  const { t } = useTranslation();
  const defaultTreatment = catalog[0]?.name || "";
  const defaultDuration = catalog[0]?.duration_min || 30;
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    time: "10:00",
    treatment: defaultTreatment,
    duration_min: defaultDuration,
    type: "trattamento",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  function pickTreatment(name) {
    const match = catalog.find((c) => (c.name || "").toLowerCase() === (name || "").toLowerCase());
    setForm((f) => ({ ...f, treatment: name, duration_min: match?.duration_min || f.duration_min }));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.treatment || !form.date || !form.time) { toast.error(t("common.error")); return; }
    setBusy(true);
    try {
      const iso = new Date(`${form.date}T${form.time}:00`).toISOString();
      await api.post("/appointments", {
        patient_id: patient.id,
        treatment: form.treatment,
        date: iso,
        duration_min: Number(form.duration_min),
        notes: form.notes,
        type: form.type,
        consent_template_id: "",
      });
      toast.success(t("calendar.form.created") || "Appuntamento creato");
      onDone();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="quickbook-overlay">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="quickbook-form"
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-stone-100">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1">{t("patients.book") || "Prenota"}</p>
            <h3 className="font-heading text-2xl font-light">{patient.full_name}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-900" data-testid="quickbook-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("calendar.form.treatment") || "Trattamento"} *</label>
            <input type="text" value={form.treatment} onChange={(e) => pickTreatment(e.target.value)} required
              data-testid="quickbook-treatment"
              list="quickbook-treatments-datalist" autoComplete="off"
              placeholder={catalog[0]?.name || "Medicina Estetica"}
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
            <datalist id="quickbook-treatments-datalist">
              {catalog.map((c) => (
                <option key={c.name} value={c.name}>{c.duration_min} min</option>
              ))}
            </datalist>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.date")} *</label>
              <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="quickbook-date"
                className="w-full px-3 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.time")} *</label>
              <input type="time" required value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                data-testid="quickbook-time"
                className="w-full px-3 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">Min</label>
              <input type="number" min="1" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
                data-testid="quickbook-duration"
                className="w-full px-3 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">Tipo</label>
            <div className="flex gap-2">
              {["trattamento", "controllo"].map((tp) => (
                <button key={tp} type="button" onClick={() => setForm({ ...form, type: tp })}
                  data-testid={`quickbook-type-${tp}`}
                  className={`flex-1 py-2 rounded-full text-sm capitalize transition-colors ${form.type === tp ? "bg-stone-900 text-white" : "border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>
                  {tp}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.notes")}</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
              data-testid="quickbook-notes"
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-stone-100 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full border border-stone-200 hover:bg-stone-50 text-sm">
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={busy} data-testid="quickbook-submit"
            className="px-5 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 text-sm disabled:opacity-50">
            {busy ? "…" : (t("common.save") || "Salva")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{label}{required && " *"}</label>
      <input type={type} value={value} required={required} placeholder={placeholder}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373] focus:ring-1 focus:ring-[#D4A373]" />
    </div>
  );
}
